/**
 * LIFECYCLE-1c display. Project state is DRAFT | ACTIVE | CLOSING | CLOSED.
 * Legacy projects.status does not decide a count or a label.
 */
export const NOT_ENOUGH_DATA = 'Not enough data';
/** ONE-TRUTH-FE-1: no health value came back, so none is claimed. */
export const NOT_COMPUTED = 'Not computed';

const KNOWN_STATES = ['DRAFT', 'ACTIVE', 'CLOSING', 'CLOSED'] as const;

export function isKnownProjectState(value: string): boolean {
  return (KNOWN_STATES as readonly string[]).includes(value);
}

/** CLOSED-READONLY-FE-2 — project content is read-only when state is CLOSED. */
export function isProjectReadOnly(state: string | null | undefined): boolean {
  return state === 'CLOSED';
}

export function activeProjectMetric(
  projects: ReadonlyArray<{ state?: string | null }>,
): number | typeof NOT_ENOUGH_DATA {
  if (projects.length === 0) return 0;
  let sawKnown = false;
  let active = 0;
  for (const project of projects) {
    const state = project.state;
    if (typeof state !== 'string' || !isKnownProjectState(state)) continue;
    sawKnown = true;
    if (state === 'ACTIVE' || state === 'CLOSING') active += 1;
  }
  if (!sawKnown) return NOT_ENOUGH_DATA;
  return active;
}

/**
 * DRAFT-TRUTH-1: a NOT-STARTED Draft is not rated. The server decides which
 * Drafts those are (a Draft with work under way IS rated — TR-2026-10-22-20)
 * and sends health NOT_RATED for them on GET /projects. This is the health
 * half of "<state> · <health>" (WorkspaceHome renders projectStateLabel
 * first, so such a row reads "Draft · Not rated").
 */
export const NOT_RATED = 'Not rated';
/** DRAFT-TRUTH-1 (TR-2026-10-23-3): health unknown — never a stored RAG. */
export const NOT_EVALUABLE = 'Not evaluable';

export function lifecycleHealthLabel(
  state?: string | null,
  health?: string | null,
): string {
  if (health) {
    const normalized = health.toLowerCase();
    if (normalized === 'not_rated') return NOT_RATED;
    // DRAFT-TRUTH-1 (TR-2026-10-23-3): the server could not evaluate it — no RAG.
    if (normalized === 'not_evaluable') return NOT_EVALUABLE;
    if (normalized === 'healthy') return 'Healthy';
    if (normalized === 'at_risk') return 'At Risk';
    if (normalized === 'blocked') return 'Blocked';
    if (normalized === 'not_enough_data') return NOT_ENOUGH_DATA;
  }
  // ONE-TRUTH-FE-1: a live project with no computed health is not "On track".
  if (state === 'ACTIVE' || state === 'CLOSING') return NOT_COMPUTED;
  if (state === 'CLOSED') return 'Complete';
  if (state === 'DRAFT') return 'Draft';
  return NOT_ENOUGH_DATA;
}

/**
 * DRAFT-TRUTH-1: a rollup row's health text. The server sends NOT_RATED for a
 * DRAFT project; every other value is shown as sent (unchanged behaviour).
 */
export function rollupHealthText(healthStatus: string): string {
  return healthStatus === 'NOT_RATED' ? 'Draft · not rated' : healthStatus;
}

export function projectStateLabel(state?: string | null): string {
  if (state === 'DRAFT') return 'Draft';
  if (state === 'ACTIVE') return 'Active';
  if (state === 'CLOSING') return 'Closing';
  if (state === 'CLOSED') return 'Closed';
  return NOT_ENOUGH_DATA;
}

export function projectStateDotClass(state: string): string {
  if (state === 'ACTIVE' || state === 'CLOSING') return 'bg-blue-500';
  if (state === 'CLOSED') return 'bg-emerald-500';
  if (state === 'DRAFT') return 'bg-slate-400';
  return 'bg-slate-300';
}

export type ProjectStateBadge = {
  key: string;
  label: string;
  count: number;
  dotClass: string;
};

/** Skips a missing count and a zero on an unknown key. Never invents 0. */
export function projectStateBadges(
  summary: Record<string, number> | null | undefined,
): ProjectStateBadge[] {
  if (!summary) return [];
  const badges: ProjectStateBadge[] = [];
  for (const [state, count] of Object.entries(summary)) {
    if (typeof count !== 'number' || !Number.isFinite(count)) continue;
    if (!isKnownProjectState(state)) {
      if (count === 0) continue;
      badges.push({
        key: state,
        label: NOT_ENOUGH_DATA,
        count,
        dotClass: 'bg-slate-300',
      });
      continue;
    }
    badges.push({
      key: state,
      label: projectStateLabel(state),
      count,
      dotClass: projectStateDotClass(state),
    });
  }
  return badges;
}

export function hasKnownProjectState(
  summary: Record<string, number> | null | undefined,
): boolean {
  if (!summary) return false;
  return Object.entries(summary).some(
    ([state, count]) =>
      isKnownProjectState(state) &&
      typeof count === 'number' &&
      Number.isFinite(count) &&
      count > 0,
  );
}

/** ACTIVE, CLOSING, or CLOSED. DRAFT is not health data. */
export function hasLifecycleBeyondDraft(
  summary: Record<string, number> | null | undefined,
): boolean {
  if (!summary) return false;
  return Object.entries(summary).some(
    ([state, count]) =>
      typeof count === 'number' &&
      Number.isFinite(count) &&
      count > 0 &&
      (state === 'ACTIVE' || state === 'CLOSING' || state === 'CLOSED'),
  );
}
